/**
 * Offset a sticky page element has to sit at so it clears the fixed chrome
 * painted over the top of the viewport (app bar, safe-area system bar).
 *
 * Vuetify's `--v-layout-top` is not usable for this. An app bar with
 * `scroll-behavior="hide"` keeps its extension (the tabs row) on screen but
 * marks the layout item inactive, so the variable reports 0 while the
 * extension is still occupying the top of the viewport. Measuring the bar's
 * own bottom edge is the only signal that stays true in every scroll state.
 *
 * Sticky elements stack: pass the rect of one that pins above and the offset
 * clears that too, so the two never overlap.
 *
 * Only applies to elements the page itself scrolls -- see scrollsWithPage.
 *
 * @param {DOMRect|null|undefined} appBarRect bounding rect of the app bar, if present
 * @param {DOMRect|null|undefined} systemBarRect bounding rect of the safe-area
 *   system bar (App.vue renders one sized to `env(safe-area-inset-top)` on
 *   notched devices), if present
 * @param {DOMRect|null|undefined} pinnedAboveRect bounding rect of a sticky
 *   element that pins above this one (the branding band above the filter bar),
 *   if present
 * @returns {number|null} offset in px, or null when there is no chrome to clear
 */
export const resolveStickyTop = (appBarRect, systemBarRect, pinnedAboveRect) => {
  if (!appBarRect && !systemBarRect) return null

  // The safe-area system bar is fixed and paints over the notch strip, so
  // pinning to the viewport top would park the element underneath it. Layouts
  // without an app bar (the focus layout the note editor runs in) still have to
  // clear it.
  const floor = systemBarRect ? Math.round(systemBarRect.bottom) : 0

  // The bar is translated off-screen rather than unmounted, so its bottom goes
  // negative once fully hidden -- clamp so the element pins below the notch.
  const top = Math.max(floor, appBarRect ? Math.round(appBarRect.bottom) : 0)

  if (!pinnedAboveRect) return top

  // Height, not `bottom`. The element above pins at this same offset, so its
  // settled bottom is always `top + height` -- while its own `bottom` is still
  // travelling with the page until it pins. Feeding a moving value back in
  // would leave this bar chasing it a frame behind, parked below where it
  // belongs whenever scroll events batch (iOS momentum scrolling).
  return top + Math.round(pinnedAboveRect.height)
}

// Overflow values that turn a box into the scrollport of its sticky
// descendants. `hidden` counts: it establishes a scroll container the user
// cannot scroll, which is exactly how a sticky child ends up with zero scroll
// range. `clip` does not establish one, and `visible` never has.
const SCROLLPORT_OVERFLOWS = ['auto', 'scroll', 'hidden']

/**
 * Whether the page scrolls a sticky element, or an element around it does.
 *
 * A sticky element pins to its nearest scrollport. When that is an element, its
 * top edge already sits below every fixed page chrome, so it must pin flush to
 * it (offset 0) -- an app-bar offset would push it down into the scrollport and
 * leave a band of content scrolling visibly above it.
 *
 * @param {Array<{isPageRoot: boolean, overflowX: string, overflowY: string}>} ancestors
 *   ancestor boxes of the sticky element, nearest first, up to and including
 *   the page root (`<body>` / `<html>`)
 * @returns {boolean} true when the page itself is the scrollport
 */
export const scrollsWithPage = ancestors => {
  const scrollport = ancestors.find(({ isPageRoot, overflowX, overflowY }) => isPageRoot
    || SCROLLPORT_OVERFLOWS.includes(overflowY)
    || SCROLLPORT_OVERFLOWS.includes(overflowX))

  return !scrollport || scrollport.isPageRoot
}
